// Photoshop-style Character & Paragraph typography engine (original implementation)

export const WEIGHTS = [
  { v: "100", l: "Thin" },
  { v: "200", l: "ExtraLight" },
  { v: "300", l: "Light" },
  { v: "400", l: "Regular" },
  { v: "500", l: "Medium" },
  { v: "600", l: "SemiBold" },
  { v: "700", l: "Bold" },
  { v: "800", l: "ExtraBold" },
  { v: "900", l: "Black" },
];

export const KERNING_OPTIONS = [
  { v: "metrics", l: "Metrics" },
  { v: "optical", l: "Optical" },
  { v: "none", l: "None" },
];

export const ANTIALIAS_OPTIONS = [
  { v: "none", l: "None" },
  { v: "sharp", l: "Sharp" },
  { v: "crisp", l: "Crisp" },
  { v: "strong", l: "Strong" },
  { v: "smooth", l: "Smooth" },
];

export function kerningCss(k) {
  if (k === "none") return "none";
  if (k === "optical") return "auto";
  return "normal"; // metrics
}

export function smoothingCss(aa) {
  switch (aa) {
    case "none": return { WebkitFontSmoothing: "none", textRendering: "optimizeSpeed" };
    case "sharp": return { WebkitFontSmoothing: "auto", textRendering: "optimizeSpeed" };
    case "crisp": return { WebkitFontSmoothing: "antialiased", textRendering: "geometricPrecision" };
    case "strong": return { WebkitFontSmoothing: "subpixel-antialiased", textRendering: "optimizeLegibility" };
    case "smooth":
    default: return { WebkitFontSmoothing: "antialiased", textRendering: "optimizeLegibility" };
  }
}

// Effective tracking → letter-spacing in px (PS tracking is 1/1000 em). Falls back to legacy letterSpacing.
export function effLetterSpacing(el, scale = 1) {
  const fs = el.fontSize || 40;
  if (el.tracking != null && el.tracking !== 0) return (el.tracking / 1000) * fs * scale;
  return (el.letterSpacing || 0) * scale;
}

// Effective line height in px
export function lineHeightPx(el, scale = 1) {
  const fs = (el.fontSize || 40) * scale;
  if (el.leadingAuto === false && el.leading) return el.leading * scale;
  if (el.leadingAuto === undefined && el.lineHeight) return el.lineHeight * fs; // legacy multiplier
  return 1.2 * fs; // Auto = 120%
}

export function autoLeading(el) {
  return Math.round((el.fontSize || 40) * 1.2);
}

export function alignToCss(a) {
  switch (a) {
    case "left": return { textAlign: "left", textAlignLast: "auto" };
    case "center": return { textAlign: "center", textAlignLast: "auto" };
    case "right": return { textAlign: "right", textAlignLast: "auto" };
    case "justifyLastLeft": return { textAlign: "justify", textAlignLast: "left" };
    case "justifyLastCenter": return { textAlign: "justify", textAlignLast: "center" };
    case "justifyLastRight": return { textAlign: "justify", textAlignLast: "right" };
    case "justifyAll": return { textAlign: "justify", textAlignLast: "justify" };
    default: return { textAlign: a || "left", textAlignLast: "auto" };
  }
}

// Reduce a 7-way alignment to a basic left/center/right (used by warp/curve renderers)
export function baseAlign(a) {
  if (a === "center" || a === "justifyLastCenter") return "center";
  if (a === "right" || a === "justifyLastRight") return "right";
  return "left";
}

// translate(baselineShift) + scale(H,V) + faux italic skew
export function buildTextTransform(el, scale = 1) {
  const t = [];
  if (el.baselineShift) t.push(`translateY(${-el.baselineShift * scale}px)`);
  const hs = (el.horizontalScale ?? 100) / 100;
  const vs = (el.verticalScale ?? 100) / 100;
  if (hs !== 1 || vs !== 1) t.push(`scale(${hs}, ${vs})`);
  if (el.fauxItalic) t.push("skewX(-12deg)");
  return t.join(" ");
}


// Dynamic text: compute the largest font size (in design px) that makes the text
// wrap-fit inside the element's width × height box (Photoshop-style "fit to frame").
let _fitMeasurer = null;
function getFitMeasurer() {
  if (_fitMeasurer && document.body.contains(_fitMeasurer)) return _fitMeasurer;
  const d = document.createElement("div");
  d.setAttribute("aria-hidden", "true");
  d.style.cssText =
    "position:absolute;left:-99999px;top:-99999px;visibility:hidden;white-space:pre-wrap;" +
    "word-break:break-word;box-sizing:border-box;padding:0;margin:0;border:0;";
  document.body.appendChild(d);
  _fitMeasurer = d;
  return d;
}

export function fitFontSize(el, minFs = 4, maxFs = 1000) {
  if (typeof document === "undefined") return el.fontSize || 40;
  const W = Math.max(1, el.width || 1);
  const H = Math.max(1, el.height || 1);
  const d = getFitMeasurer();
  d.style.width = `${W}px`;
  d.style.fontFamily = `'${el.fontFamily}', sans-serif`;
  d.style.fontWeight = el.fontWeight || 400;
  d.style.fontStyle = el.fontStyle || "normal";
  d.textContent = el.text && el.text.length ? el.text : " ";
  d.style.whiteSpace = "pre-wrap";
  const fits = (fs) => {
    d.style.fontSize = `${fs}px`;
    d.style.lineHeight = `${lineHeightPx({ ...el, fontSize: fs }, 1)}px`;
    d.style.letterSpacing = `${effLetterSpacing({ ...el, fontSize: fs }, 1)}px`;
    return d.scrollHeight <= H + 0.5 && d.scrollWidth <= W + 0.5;
  };
  if (fits(maxFs)) return Math.floor(maxFs); // already fits at the cap (e.g. shrink mode, no overflow)
  let lo = minFs, hi = maxFs, best = minFs;
  for (let i = 0; i < 20; i++) {
    const mid = (lo + hi) / 2;
    if (fits(mid)) { best = mid; lo = mid; } else { hi = mid; }
  }
  return Math.max(minFs, Math.floor(best));
}


// "Fill width" mode: scale EACH line independently so its natural (no-wrap) width
// equals the box width. Returns an array of design-px font sizes, one per line.
export function fitLineSizes(el, ref = 200, minFs = 4, maxFs = 4000) {
  if (typeof document === "undefined") return null;
  const W = Math.max(1, el.width || 1);
  const LS = ((el.tracking || 0) / 1000) * 100; // uniform design-px letter-spacing (matches render)
  const d = getFitMeasurer();
  d.style.width = "auto";
  d.style.whiteSpace = "nowrap";
  d.style.lineHeight = "normal";
  d.style.fontFamily = `'${el.fontFamily}', sans-serif`;
  d.style.fontWeight = el.fontWeight || 400;
  d.style.fontStyle = el.fontStyle || "normal";
  d.style.fontSize = `${ref}px`;
  d.style.letterSpacing = "0px"; // measure glyph-only; tracking added as fixed px below
  const lines = (el.text || "").split("\n");
  const sizes = lines.map((ln) => {
    if (!ln.trim()) return el.fontSize || 40;
    d.textContent = ln;
    const g0 = d.scrollWidth || 1;
    const avail = W - ln.length * LS; // CSS adds LS after each char
    if (avail <= 1) return minFs;
    return Math.max(minFs, Math.min(maxFs, Math.floor((avail * ref) / g0)));
  });
  // restore shared measurer for reuse
  d.style.whiteSpace = "pre-wrap";
  d.style.width = `${W}px`;
  return sizes;
}
